import { Flame, Clock, AlertTriangle, Trophy } from "lucide-react";
import type { BurnOverall, BurnKpis, BurnRow } from "@/api/types";
import { fmt } from "@/lib/format";
import { BurnStatWidget } from "./BurnStatWidget";

export interface BurnStatsRowProps {
  overall: BurnOverall;
  kpis: BurnKpis;
  rows: BurnRow[];
}

/** Battle-stats row: 4 animated tiles summarising fleet-wide burn pressure.
 * Derivations:
 *  - velocity  = kpis.daily_burn_rate (credits/day, fleet aggregate)
 *  - runway    = (overall.cap - overall.used) / daily_burn_rate
 *  - critical% = rows with pct >= 95 / total rows
 *  - top user  = max(projected_month_end / 30) across rows */
export function BurnStatsRow({ overall, kpis, rows }: BurnStatsRowProps) {
  const velocity = Math.max(0, kpis.daily_burn_rate);

  const remaining = Math.max(0, overall.cap - overall.used);
  const runwayDays =
    velocity > 0 ? Math.floor(remaining / velocity) : Number.POSITIVE_INFINITY;
  const runwayTone: "safe" | "warn" | "over" =
    runwayDays > 30 ? "safe" : runwayDays >= 15 ? "warn" : "over";
  const runwayDisplay = Number.isFinite(runwayDays) ? runwayDays : 999;

  const total = rows.length;
  const critical = rows.filter((r) => r.pct >= 95).length;
  const criticalPct = total > 0 ? (critical / total) * 100 : 0;
  const criticalPulse = criticalPct > 10;

  // Top burner: use projected_month_end as a fleet-neutral "heat" signal.
  let topRow: BurnRow | null = null;
  for (const r of rows) {
    if (!topRow || r.projected_month_end > topRow.projected_month_end) {
      topRow = r;
    }
  }
  const topDaily = topRow ? Math.round(topRow.projected_month_end / 30) : 0;
  const topLabel = topRow ? topRow.username : "—";

  return (
    <section
      aria-label="Estadísticas de combate"
      className="grid grid-cols-2 gap-3 md:grid-cols-4"
    >
      <BurnStatWidget
        icon={<Flame className="h-5 w-5" strokeWidth={2} />}
        label="Velocidad de quema"
        value={velocity}
        format={(n) => fmt(n)}
        sublabel="créditos/día · flota"
        tone="neutral"
      />
      <BurnStatWidget
        icon={<Clock className="h-5 w-5" strokeWidth={2} />}
        label="Días de autonomía"
        value={runwayDisplay}
        format={(n) => (Number.isFinite(runwayDays) ? `${Math.round(n)}` : "∞")}
        sublabel={
          runwayTone === "safe"
            ? "margen cómodo"
            : runwayTone === "warn"
              ? "atención al ritmo"
              : "quema crítica"
        }
        tone={runwayTone}
      />
      <BurnStatWidget
        icon={<AlertTriangle className="h-5 w-5" strokeWidth={2} />}
        label="Flota al 95%+"
        value={criticalPct}
        format={(n) => `${n.toFixed(1)}%`}
        sublabel={`${critical} de ${total} usuarios`}
        tone={criticalPulse ? "over" : criticalPct > 0 ? "warn" : "safe"}
        pulse={criticalPulse}
      />
      <BurnStatWidget
        icon={<Trophy className="h-5 w-5" strokeWidth={2} />}
        label="Top quemador"
        value={topDaily}
        format={(n) => fmt(n)}
        sublabel={topLabel}
        tone="warn"
      />
    </section>
  );
}
